import { CostChannel } from '../../../../../../graphql-schema/src/schema-types.generated';
import {
  useHomePickupMenuQuery,
  useHomeDeliveryMenuQuery,
  HomeMenuFragment,
} from './graphql/HomeMenu.generated';
import { useHomeMenuRefreshContext } from './hooks';

export const useHomeMenu = (params: UseHomeMenuParams) => {
  const { restaurantId, addressId, pause = false } = params;

  const refreshContext = useHomeMenuRefreshContext();

  const [pickupMenuQueryResponse] = useHomePickupMenuQuery({
    pause: pause || !restaurantId || Boolean(addressId),
    requestPolicy: 'cache-and-network',
    context: refreshContext,
    variables: {
      id: restaurantId || '',
      costChannel: CostChannel.PickupCostChannel,
    },
  });

  const [deliveryMenuQueryResponse] = useHomeDeliveryMenuQuery({
    pause: pause || !addressId,
    requestPolicy: 'cache-and-network',
    context: refreshContext,
    variables: {
      input: { addressId },
      costChannel: CostChannel.DeliveryCostChannel,
    },
  });

  const isLoadingMenu = pickupMenuQueryResponse.fetching || deliveryMenuQueryResponse.fetching;

  const pickupMenuData = pickupMenuQueryResponse.data;
  const pickupMenu = pickupMenuData?.restaurant;

  const deliveryMenuData = deliveryMenuQueryResponse.data?.locationByAddress;
  const deliveryLocation = deliveryMenuData?.__typename === 'Location' ? deliveryMenuData : undefined; // prettier-ignore

  const restaurant = addressId ? deliveryLocation?.restaurant : pickupMenu;
  const categories = restaurant?.menu?.categories;
  const products = categories?.flatMap((category) => category.products?.map(prepareProduct));

  return {
    restaurant,
    deliveryFee: deliveryLocation?.deliveryFee,
    products,
    isLoadingMenu,
  };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function prepareProduct(product: HomeProduct) {
  return {
    id: product.id,
    slug: product.slug,
    baseProductSlug: product.baseProduct?.slug,
    lineageKey: product.baseProduct?.lineageKey,
    isModifiable: product.isModifiable,
    title: product.name,
    description: product.description,
    cost: product.cost,
    cloudinaryImage: product.asset?.url,
    allergenProperties: product.allergenProperties,
    calories: product.baseProduct?.calories,
    totalFatG: product.baseProduct?.totalFatG,
    totalCarbsG: product.baseProduct?.totalCarbsG,
    dietaryFiberG: product.baseProduct?.dietaryFiberG,
    sugarsG: product.baseProduct?.sugarsG,
    proteinG: product.baseProduct?.proteinG,
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type HomeProduct = HomeMenuFragment['menu']['categories'][number]['products'][number];

type UseHomeMenuParams = {
  restaurantId?: string;
  addressId?: string;
  pause?: boolean;
};
